import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { Accordion, Box, Button, FormControl, Stack } from '@mui/material';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import SettingsKey from 'app/profile/settings/utils/settingsTypes';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getSettingsDirty } from 'redux/slices/profileSlice';
import { getSettingsEmailPending } from 'redux/slices/settingSlice';
import { getSavePending } from 'redux/slices/templateSlice';

import useColorsStyles from 'common/colors';
import CircularProgressWithLabel from 'common/components/CircularProgressWithLabel';
import CustomTextField from 'common/components/CustomTextField';
import ImageFileInput from 'common/components/ImageFileInput';
import UserRole from 'common/enums/roles';
import useFlexBoxStyles from 'common/flexBox';
import { useAbortController } from 'common/hooks';
import useOnliveStyles from 'common/onliveStyles';
import { handleUploadFileSignedUrl } from 'common/utils/file';
import { getUser } from 'redux/slices/authSlice';
import ItemSettings from '../components/ItemSettings';
import useStylesFieldForm from '../styles/useStylesFieldForm.styles';
import useStylesSettings from '../styles/useStylesSettings.styles';
import CommunicationPreferences from './CommunicationPreferences';

const CommunicationSettings = (props) => {
    const {
        communicationPreferences,
        toggleChange,
        handleSubmit,
        hasChanges,
        // senderNameValue,
        // verifiedIdentities,
        // shareEmailIdentities,
        isAuthorize,
        smsCommunicationEnabled,
        formatDateEmail,
        formatTimeEmail,
        formatDateSms,
        formatTimeSms,
        smsSenderName,
        smsSenderId,
        qrCenterImageValue,
    } = props;

    const { t } = useTranslation();
    const { classes: classes1 } = useStylesFieldForm();
    const { classes: classes2 } = useStylesSettings();
    const classes = { ...classes1, ...classes2 };
    const { classes: onlive } = useOnliveStyles();
    const { classes: flexBox, cx } = useFlexBoxStyles();
    const { classes: colors } = useColorsStyles();
    const savePending = useSelector(getSavePending);
    const settingsPending = useSelector(getSettingsEmailPending);
    const settingsDirty = useSelector(getSettingsDirty);
    const user = useSelector(getUser);
    const { enqueueSnackbar } = useSnackbar();
    const getSignal = useAbortController();
    const [uploadingQrCenterImage, setUploadingQrCenterImage] = useState(false);
    const [uploadQrCenterImageProgress, setUploadQrCenterImageProgress] = useState(0);
    // const [senderNameSetting, setSenderNameSetting] = useState(isString(senderNameValue) ? [] : senderNameValue);
    // const [verifiedIdentitiesSetting, setVerifiedIdentitiesSetting] = useState(verifiedIdentities);
    // const [shareEmailIdentitiesSetting, setShareEmailIdentitiesSetting] = useState(shareEmailIdentities);

    // const handleShareEmailIdentitiesChange = (e) => {
    //     setShareEmailIdentitiesSetting(e.target.checked);
    //     toggleChange(SettingsKey.SHARE_EMAIL_IDENTITIES, e.target.checked);
    // };

    const handleCheckChange = (event, settingName) => {
        toggleChange(settingName, event.target.checked);
    };

    /*  useEffect(() => {
        if (!_.isEqual(senderNameSetting, senderNameValue)) {
            toggleChange(SettingsKey.EMAIL_SENDER_NAME, senderNameSetting);
        }
    }, [senderNameSetting, toggleChange, senderNameValue]);

    useEffect(() => {
        if (!_.isEqual(verifiedIdentitiesSetting, verifiedIdentities)) {
            toggleChange(SettingsKey.ORGANIZATION_EMAIL_IDENTITIES, verifiedIdentitiesSetting);
        }
    }, [verifiedIdentitiesSetting, toggleChange, verifiedIdentities]); */

    const handleQrCenterImageUploadFile = (file) => {
        if (file.size / 1000 > 500) {
            enqueueSnackbar(t('upload.imageSizeError', { size: '500kb' }), { variant: 'error' });
            return;
        }
        handleUploadFileSignedUrl({
            file,
            size: 1024 * 1024 * 100, // 100MB
            onInit: () => {
                setUploadQrCenterImageProgress(0);
                setUploadingQrCenterImage(true);
            },
            onProgress: (loaded, total) => {
                setUploadQrCenterImageProgress(Math.min(100, Math.round((loaded / total) * 100)));
            },
            onComplete: (url) => {
                setUploadingQrCenterImage(false);
                toggleChange(SettingsKey.QR_CENTER_IMAGE, url);
            },
            onError: (error) => {
                setUploadingQrCenterImage(false);
                if (error.code !== 'ERR_CANCELED') {
                    enqueueSnackbar(t('app.common.uploadError'), { variant: 'error' });
                }
            },
            bucketType: 'resources',
            signal: getSignal(),
        });
    };

    const renderEmailSettings = ({ disabled, disabledEditEmail, isMultiProfile, disabledEditEmailTemplate }) => (
        <React.Fragment>
            <Accordion disabled={disabled} className={classes.accordion}>
                <AccordionSummary
                    expandIcon={<ArrowRightIcon className={classes.accordionIcon} />}
                    aria-controls="panel2a-content"
                    id="panel2a-header"
                >
                    <Typography variant="subtitle1" fontWeight={500}>
                        {t('profile.settings.communications')}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails className={classes.eventSection}>
                    <Box display="flex" justifyContent="flex-end" alignItems="center" style={{ width: '100%' }}>
                        <Box sx={{ display: settingsDirty && hasChanges ? 'inline-flex' : 'none' }}>
                            <Button
                                variant="contained"
                                className={classes.redColor}
                                size="small"
                                onClick={handleSubmit}
                            >
                                {t('profile.settings.emailsSettings.save')}
                            </Button>
                        </Box>
                    </Box>

                    <Stack spacing={3} direction="column">
                        <Stack direction="column">
                            <Typography variant="onliveH6" fontWeight={500}>
                                {t('settings.section.email')}
                            </Typography>
                            <Stack
                                spacing={2}
                                direction="column"
                                className={cx(flexBox.borderRadius1, onlive.borderMediumGray, flexBox.padding1)}
                            >
                                {/* <FormControl style={{ width: '100%', margin: '5px 0 0 0', display: 'block' }}>
                                    <Typography style={{ width: '100%', margin: '8px 0 0 0', display: 'block' }}>
                                        {t('profile.settings.emailsSettings.senderName')}
                                    </Typography>
                                    <SenderName
                                        disabledEditEmail={disabledEditEmail}
                                        senderNameSetting={senderNameSetting}
                                        setSenderNameSetting={setSenderNameSetting}
                                    />
                                </FormControl>
                                <Stack spacing={2} direction="column">
                                    <Typography style={{ width: '100%', margin: '8px 0 0 0', display: 'block' }}>
                                        {t('profile.settings.emailsSettings.emailIdentities')}
                                    </Typography>
                                    {verifiedIdentities?.length > 0 && isMultiProfile && (
                                        <FormControl style={{ width: '100%', margin: '5px 0 0 0', display: 'block' }}>
                                            <WrapWithTooltip
                                                style={{ width: '100%' }}
                                                title={messageNotAuthorize}
                                                wrap={disabledEditEmail}
                                                placement={'left'}
                                            >
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={shareEmailIdentitiesSetting}
                                                            onChange={handleShareEmailIdentitiesChange}
                                                            name="active"
                                                            color="primary"
                                                        />
                                                    }
                                                    label={
                                                        <FormLabel>
                                                            {t('profile.settings.emailsSettings.shareEmailIdentities')}
                                                        </FormLabel>
                                                    }
                                                />
                                            </WrapWithTooltip>
                                        </FormControl>
                                    )}
                                    <FormControl style={{ width: '100%', margin: '5px 0 0 0', display: 'block' }}>
                                        <EmailIdentity
                                            disabledEditEmail={disabledEditEmail}
                                            verifiedIdentitiesSetting={verifiedIdentitiesSetting}
                                            setVerifiedIdentitiesSetting={setVerifiedIdentitiesSetting}
                                        />
                                    </FormControl>
                                </Stack> */}

                                <Stack direction="column">
                                    <Typography className={classes.text}>
                                        {t('settings.events.formatDateEmailTitle')}
                                    </Typography>

                                    <Box style={{ width: '60%' }}>
                                        <Box display="flex" flexDirection="column" style={{ width: '100%' }}>
                                            <FormControl>
                                                <CustomTextField
                                                    value={formatDateEmail}
                                                    onChange={(e) =>
                                                        toggleChange(SettingsKey.FORMAT_DATE_EMAIL, e.target.value)
                                                    }
                                                    style={{ width: '100%', marginTop: '10px' }}
                                                    variant="outlined"
                                                    size="small"
                                                    disabled={savePending || settingsPending}
                                                />
                                            </FormControl>
                                        </Box>
                                    </Box>
                                </Stack>

                                <Stack direction="column">
                                    <Typography className={classes.text}>
                                        {t('settings.events.formatTimeEmailTitle')}
                                    </Typography>

                                    <Box style={{ width: '60%' }}>
                                        <Box display="flex" flexDirection="column" style={{ width: '100%' }}>
                                            <FormControl>
                                                <CustomTextField
                                                    value={formatTimeEmail}
                                                    onChange={(e) =>
                                                        toggleChange(SettingsKey.FORMAT_TIME_EMAIL, e.target.value)
                                                    }
                                                    style={{ width: '100%', marginTop: '10px' }}
                                                    variant="outlined"
                                                    size="small"
                                                    disabled={savePending || settingsPending}
                                                />
                                            </FormControl>
                                        </Box>
                                    </Box>
                                </Stack>
                            </Stack>
                        </Stack>
                        <Stack direction="column">
                            <Typography variant="onliveH6" fontWeight={500}>
                                {t('settings.section.sms')}
                            </Typography>
                            <Stack
                                direction="column"
                                spacing={2}
                                className={cx(flexBox.borderRadius1, onlive.borderMediumGray, flexBox.padding1)}
                            >
                                <ItemSettings
                                    label={t('settings.newFeaturesSettings.enableSmsCommunication')}
                                    onChange={handleCheckChange}
                                    name={'smsCommunicationEnabled'}
                                    keySettings={SettingsKey.SMS_ENABLED}
                                    value={smsCommunicationEnabled}
                                    paddingBottom
                                />

                                {smsCommunicationEnabled ? (
                                    <>
                                        <Stack direction="column">
                                            <Typography className={classes.text}>
                                                {t('settings.communications.smsSenderName')}
                                            </Typography>

                                            <Box style={{ width: '60%' }}>
                                                <Box display="flex" flexDirection="column" style={{ width: '100%' }}>
                                                    <FormControl>
                                                        <CustomTextField
                                                            value={smsSenderName}
                                                            onChange={(e) =>
                                                                toggleChange(
                                                                    SettingsKey.SMS_SENDER_NAME,
                                                                    e.target.value,
                                                                )
                                                            }
                                                            style={{ width: '100%', marginTop: '10px' }}
                                                            variant="outlined"
                                                            size="small"
                                                            disabled={savePending || settingsPending}
                                                        />
                                                    </FormControl>
                                                </Box>
                                            </Box>
                                        </Stack>
                                        <Stack direction="column">
                                            <Typography className={classes.text}>
                                                {t('settings.communications.smsSenderId')}
                                            </Typography>

                                            <Box style={{ width: '60%' }}>
                                                <Box display="flex" flexDirection="column" style={{ width: '100%' }}>
                                                    <FormControl>
                                                        <CustomTextField
                                                            value={smsSenderId}
                                                            onChange={(e) =>
                                                                toggleChange(SettingsKey.SMS_SENDER_ID, e.target.value)
                                                            }
                                                            style={{ width: '100%', marginTop: '10px' }}
                                                            variant="outlined"
                                                            size="small"
                                                            disabled={savePending || settingsPending}
                                                        />
                                                    </FormControl>
                                                    <Stack direction="row" alignItems="start" m={1}>
                                                        <Typography fontWeight={600} className={cx(colors.textWarning)}>
                                                            {t('app.common.warning')}:&nbsp;
                                                        </Typography>
                                                        <Typography fontWeight={400} className={cx(colors.textWarning)}>
                                                            {t('settings.communications.smsSenderIdWarning')}
                                                        </Typography>
                                                    </Stack>
                                                </Box>
                                            </Box>
                                        </Stack>
                                        <Stack direction="column">
                                            <Typography className={classes.text}>
                                                {t('settings.communications.formatDateSms')}
                                            </Typography>

                                            <Box style={{ width: '60%' }}>
                                                <Box display="flex" flexDirection="column" style={{ width: '100%' }}>
                                                    <FormControl>
                                                        <CustomTextField
                                                            value={formatDateSms}
                                                            onChange={(e) =>
                                                                toggleChange(
                                                                    SettingsKey.FORMAT_DATE_SMS,
                                                                    e.target.value,
                                                                )
                                                            }
                                                            style={{ width: '100%', marginTop: '10px' }}
                                                            variant="outlined"
                                                            size="small"
                                                            disabled={savePending || settingsPending}
                                                        />
                                                    </FormControl>
                                                </Box>
                                            </Box>
                                        </Stack>

                                        <Stack direction="column">
                                            <Typography className={classes.text}>
                                                {t('settings.communications.formatTimeSms')}
                                            </Typography>

                                            <Box style={{ width: '60%' }}>
                                                <Box display="flex" flexDirection="column" style={{ width: '100%' }}>
                                                    <FormControl>
                                                        <CustomTextField
                                                            value={formatTimeSms}
                                                            onChange={(e) =>
                                                                toggleChange(
                                                                    SettingsKey.FORMAT_TIME_SMS,
                                                                    e.target.value,
                                                                )
                                                            }
                                                            style={{ width: '100%', marginTop: '10px' }}
                                                            variant="outlined"
                                                            size="small"
                                                            disabled={savePending || settingsPending}
                                                        />
                                                    </FormControl>
                                                </Box>
                                            </Box>
                                        </Stack>
                                    </>
                                ) : null}
                            </Stack>
                        </Stack>
                        {user.role === UserRole.ROOT ? (
                            <CommunicationPreferences
                                smsCommunicationEnabled={smsCommunicationEnabled}
                                communicationPreferences={communicationPreferences}
                                toggleChange={toggleChange}
                            />
                        ) : null}
                        <Stack direction="column">
                            <Typography variant="onliveH6" fontWeight={500}>
                                {t('settings.section.qrCenterImage')}
                            </Typography>
                            <Stack
                                spacing={1}
                                direction="column"
                                className={cx(flexBox.borderRadius1, onlive.borderMediumGray, flexBox.padding1)}
                            >
                                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        flexShrink={0}
                                        width={88}
                                        height={88}
                                        overflow="hidden"
                                        sx={{
                                            border: '1px dashed',
                                            borderColor: qrCenterImageValue ? 'transparent' : 'divider',
                                            borderRadius: 2,
                                            backgroundColor: 'action.hover',
                                        }}
                                    >
                                        {qrCenterImageValue ? (
                                            <img
                                                src={qrCenterImageValue}
                                                alt={t('settings.section.qrCenterImage')}
                                                style={{ maxWidth: '80%', maxHeight: '80%', objectFit: 'contain' }}
                                            />
                                        ) : (
                                            <ImageOutlinedIcon color="disabled" fontSize="large" />
                                        )}
                                    </Box>
                                    <Stack spacing={1} direction="column" alignItems="flex-start">
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <label htmlFor="raised-button-qr-center-image-file">
                                                <Button
                                                    component="span"
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={
                                                        uploadingQrCenterImage ? null : <FileUploadOutlinedIcon />
                                                    }
                                                    disabled={uploadingQrCenterImage}
                                                >
                                                    {uploadingQrCenterImage ? (
                                                        <CircularProgressWithLabel value={uploadQrCenterImageProgress} />
                                                    ) : (
                                                        t('profile.organization.upload')
                                                    )}
                                                </Button>
                                            </label>
                                            <ImageFileInput
                                                inputId={'raised-button-qr-center-image-file'}
                                                uploadAssetFn={handleQrCenterImageUploadFile}
                                                disabled={uploadingQrCenterImage}
                                            />
                                            {qrCenterImageValue && (
                                                <Button
                                                    size="small"
                                                    color="inherit"
                                                    startIcon={<DeleteOutlinedIcon />}
                                                    disabled={uploadingQrCenterImage}
                                                    onClick={() => toggleChange(SettingsKey.QR_CENTER_IMAGE, null)}
                                                >
                                                    {t('settings.section.qrCenterImageRemove')}
                                                </Button>
                                            )}
                                        </Box>
                                        <Typography variant="caption" color="text.secondary">
                                            {t('settings.section.qrCenterImageRecommended')}
                                        </Typography>
                                    </Stack>
                                </Box>
                            </Stack>
                        </Stack>
                    </Stack>
                </AccordionDetails>
            </Accordion>
        </React.Fragment>
    );

    return (
        <>
            {renderEmailSettings({
                disabled: false,
                disabledEditEmail: !isAuthorize(['full_look_and_feel']),
                isMultiProfile: isAuthorize(['multiProfile'], true),
                disabledEditEmailTemplate: user.role === 'creator' || user.userGroupId,
            })}
        </>
    );
};

export default CommunicationSettings;
